import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ActiveDescendantKeyManager, Highlightable } from '@angular/cdk/a11y';
import { Action, Store, select } from '@ngrx/store';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import hotkeys from 'hotkeys-js';
import { ZefReactiveComponent } from '@zerops/zef/core';
import {
  ZefDialogModule,
  selectZefDialogState,
  zefDialogClose,
  zefDialogToggle
} from '@zerops/zef/dialog';
import { ZefFuseModule } from '@zerops/zef/fuse';
import { ZefScrollbarComponent } from '@zerops/zef/scrollbar';
import { ThemeService } from '@zerops/zef/theme';
import { zefGo, selectZefNgrxRouterIdByKey } from '@zerops/zef/ngrx-router';
import {
  ClientUserRole,
  canCreateProjects,
  canViewFinances,
  hasAdminRoleOrHigher,
  hasProjectFullAccess
} from '@zerops/models/client-user';
import type { ClientUser } from '@zerops/models/client-user';
import type { ProjectWithServiceStacks } from '@zerops/models/project';
import { ServiceStackTypes } from '@zerops/models/service-stack';
import { KeyChipModule } from '@zerops/zui/key-chip';
import { ZuiThreeDotsModule } from '@zerops/zui/three-dots';
import { RouteKeys } from '@zerops/zerops/app';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import { ServiceStackEntity } from '@zerops/zerops/core/service-stack-base';
import {
  selectServicesWithAccessDetail,
  selectServicesWithBackup,
  selectServicesWithConfiguration
} from '@zerops/zerops/core/settings-base';
import { ClientUserEntity } from '@zerops/zerops/core/client-user-base';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { Subject, combineLatest, forkJoin, of } from 'rxjs';
import {
  auditTime,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  timeout
} from 'rxjs/operators';
import isEqual from 'lodash-es/isEqual';
import { FEATURE_NAME } from './command-palette.constant';
import {
  PaletteEntry,
  PaletteFlatEntry,
  PaletteGroup,
  PaletteIdLookup,
  PaletteItem,
  PaletteMatch,
  PalettePermissions,
  PaletteRecent,
  PaletteRouteContext
} from './command-palette.model';
import { CommandPaletteApi } from './command-palette.api';
import {
  buildProjectContextCommands,
  buildServiceActionCommands,
  buildServiceContextCommands,
  buildStaticCommands,
  ServiceTabGating
} from './command-palette.commands';

// ctrl+k stays free on macOS — it's the native kill-to-end-of-line
// binding inside text fields
const IS_MAC = /mac/i.test(navigator.platform);
const HOTKEY = IS_MAC ? 'command+k' : 'ctrl+k';
const RECENTS_KEY = 'z-palette-recents';
const RECENTS_CAP = 50;
const RECENTS_SHOWN = 6;
const RECENT_SCORE_BOOST = 0.25;
const RESULTS_CAP = 50;
// zerops entity ids are 22-char base64url strings — anything close enough
// (single token, no spaces) is worth an exact-id API lookup; a false
// positive just returns zero hits
const ID_LIKE = /^[A-Za-z0-9_-]{16,}$/;
const ID_LOOKUP_DEBOUNCE = 250;
const ID_LOOKUP_TIMEOUT = 10000;
// well above any local score — an exact id hit is what the user pasted for
const ID_LOOKUP_SCORE = 100;
const ID_LOOKUP_GROUP = 'Found by ID';
const EMPTY_ID_LOOKUP: PaletteIdLookup = {
  forId: '',
  pending: false,
  containers: [],
  appVersions: []
};
// default-state order follows usage frequency: context, then the user's
// own entities, then chrome; Actions / Pages only occur in scoped views
const GROUP_ORDER = [
  'Actions',
  'Pages',
  'Current service',
  'Current project',
  'Projects',
  'Services',
  'Navigation',
  'Quick actions',
  'Team',
  'Interface',
  'Resources',
  'Session'
];
// keeps the default (empty query) state scannable — deep content
// is one keystroke away
const EMPTY_STATE_GROUP_CAPS: Record<string, number> = {
  Projects: 5,
  Services: 5,
  Team: 3
};

interface PaletteScope {
  type: 'project' | 'service';
  id: string;
  label: string;
}

// plain-data projection of the entity slices — everything the item
// builders, scope seeding and action gating need, and nothing that
// churns (ids, names and access flags are stable, so the isEqual gate
// stays effective)
interface PaletteEntityIndex {
  activeClientUserId?: string;
  projects: Array<{
    id: string;
    name: string;
    fullAccess: boolean;
    stacks: Array<{
      id: string;
      name: string;
      typeId?: ServiceStackTypes;
      typeCategory?: string;
      reloadAvailable: boolean;
    }>;
  }>;
  users: Array<{
    id: string;
    userId?: string;
    name: string;
    email?: string;
  }>;
}

// id → display / gating metadata, for scope-chip labels and service
// action gating
interface PaletteEntityMeta {
  label: string;
  parentId?: string;
  fullAccess: boolean;
  typeId?: ServiceStackTypes;
  typeCategory?: string;
  reloadAvailable?: boolean;
}

const EMPTY_ENTITY_INDEX: PaletteEntityIndex = { projects: [], users: [] };

interface ScoredItem {
  score: number;
  labelRanges: number[][];
  sublabelRanges: number[][];
}

// strip diacritics so "cestina" finds "Čeština"; precomposed characters
// map 1:1 to base characters, keeping highlight ranges aligned
const fold = (value: string) => value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

// adapter between a rendered option row and the cdk key manager —
// activation is reported back through the callback so the template
// stays signal-driven
class PaletteOption implements Highlightable {
  disabled = false;

  constructor(
    public entry: PaletteFlatEntry,
    private _activate: (entry: PaletteFlatEntry) => void
  ) {}

  setActiveStyles() {
    this._activate(this.entry);
  }

  setInactiveStyles() {
    // active state is fully derived from the activeIndex signal
  }
}

@Component({
  selector: 'z-command-palette',
  templateUrl: './command-palette.feature.html',
  styleUrl: './command-palette.feature.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    ZefDialogModule,
    ZefFuseModule,
    ZefScrollbarComponent,
    KeyChipModule,
    ZuiThreeDotsModule
  ]
})
export class CommandPaletteFeature extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #theme = inject(ThemeService);
  #projectEntity = inject(ProjectEntity);
  #serviceStackEntity = inject(ServiceStackEntity);
  #clientUserEntity = inject(ClientUserEntity);
  #userEntity = inject(UserEntity);
  #api = inject(CommandPaletteApi);
  searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInputRef');

  // # Event Streams
  onClose$ = new Subject<void>();
  onRun$ = new Subject<PaletteItem>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  dialogOptions = {
    disableClose: false,
    width: '640px',
    maxWidth: '92vw',
    // anchored near the top so the panel never re-centers
    // while results change
    position: { top: '12vh' },
    // keyboard-triggered surface used constantly — instant beats animated
    enterAnimationDuration: '0ms',
    exitAnimationDuration: '0ms'
  };
  #previousHotkeysFilter = hotkeys.filter;
  #keyManager: ActiveDescendantKeyManager<PaletteOption>;
  #lastPointer: { x: number; y: number };
  // id of the active item — lets the key manager rebuild preserve the
  // selection when the list identity changes under the user
  #activeItemId: string | null = null;
  #previouslyFocused: HTMLElement | null = null;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => !!data?.state));
  #activeClientUser$ = this.#userEntity.activeClientUser$;
  #permissions$ = this.#activeClientUser$.pipe(
    filter((d) => !!d),
    map((clientUser): PalettePermissions => ({
      canCreateProjects: canCreateProjects(clientUser),
      canViewFinances: canViewFinances(clientUser),
      hasAdminRoleOrHigher: hasAdminRoleOrHigher(clientUser.roleCode),
      hasOwnerRole: clientUser.roleCode === ClientUserRole.Owner
    })),
    distinctUntilChanged(isEqual)
  );
  #staticCommands$ = this.#permissions$.pipe(
    map((permissions) => buildStaticCommands({
      dispatch: (action) => this.#store.dispatch(action),
      theme: this.#theme,
      permissions
    }))
  );
  // the router serializer only keeps the DEEPEST route's own params, and
  // `:id` lives on a parent route — with Angular's default 'emptyOnly'
  // inheritance the child pages never see it, so `params.id` is useless
  // here; the activation guards write the id into the ngrx-router idMap
  // instead, which is how the whole app resolves "current entity"
  #routeContext$ = combineLatest([
    this.#store.pipe(select(selectZefNgrxRouterIdByKey(RouteKeys.ProjectDetail))),
    this.#store.pipe(select(selectZefNgrxRouterIdByKey(RouteKeys.ServiceStackDetail)))
  ]).pipe(
    map(([ projectId, serviceId ]): PaletteRouteContext => {
      if (serviceId) {
        return { type: 'service', id: serviceId };
      }
      if (projectId) {
        return { type: 'project', id: projectId };
      }
      return { type: 'none' };
    }),
    distinctUntilChanged((a, b) => a.type === b.type && a.id === b.id)
  );
  #context$ = this.#routeContext$.pipe(
    // fresh entityById$ stream per id — the comparator misbehaves across
    // id changes, so always switch to a new stream
    switchMap((ctx) => ctx.type === 'service'
      ? this.#serviceStackEntity.entityById$(ctx.id).pipe(
          map((stack): PaletteRouteContext => ({
            ...ctx,
            projectId: stack?.projectId ?? stack?.project?.id
          })),
          startWith(ctx)
        )
      : of(ctx)),
    // entityById$ re-emits on every entities-slice change — only rebuild
    // the context commands when the resolved context actually changes
    distinctUntilChanged((a, b) => a.type === b.type && a.id === b.id && a.projectId === b.projectId)
  );
  // the untagged project / service-stack / client-user lists are populated
  // globally at login and kept fresh over websocket — no extra subscriptions
  // needed; auditTime tames the constant entities-slice churn, and the
  // plain-data projection + isEqual gate keeps the downstream computeds
  // (items, meta lookup) stable so the key manager only rebuilds on real
  // data changes
  #entityIndex$ = combineLatest([
    this.#projectEntity.listWithServiceStacks$(),
    this.#clientUserEntity.list$(),
    this.#activeClientUser$.pipe(startWith(undefined as ClientUser))
  ]).pipe(
    auditTime(300),
    map(([ projects, clientUsers, activeClientUser ]): PaletteEntityIndex => ({
      activeClientUserId: activeClientUser?.id,
      projects: ((projects || []) as ProjectWithServiceStacks[]).map((project) => ({
        id: project.id,
        name: project.name,
        // write access decides whether service action commands exist at
        // all — computed inside the isEqual gate, it changes rarely
        fullAccess: hasProjectFullAccess(project, activeClientUser),
        // build / prepare-runtime stacks are already stripped by the entity
        // stream; `isSystem` drops the rest of the internal set (project core,
        // L7 balancer) the same way every user-facing service list does
        stacks: (project.serviceStacks || []).filter((stack) => !stack.isSystem).map((stack) => ({
          id: stack.id,
          name: stack.name,
          typeId: stack.serviceStackTypeId || undefined,
          typeCategory: stack.serviceStackTypeInfo?.serviceStackTypeCategory,
          reloadAvailable: !!stack.reloadAvailable
        }))
      })),
      users: ((clientUsers || []) as ClientUser[]).map((clientUser) => ({
        id: clientUser.id,
        userId: clientUser.userId,
        name: clientUser.user?.fullName || clientUser.user?.email || 'Unknown user',
        email: clientUser.user?.email
      }))
    })),
    distinctUntilChanged(isEqual),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // -- signals
  #staticCommands = toSignal(this.#staticCommands$, { initialValue: [] as PaletteItem[] });
  #context = toSignal(this.#context$, { initialValue: { type: 'none' } as PaletteRouteContext });
  #entityIndex = toSignal(this.#entityIndex$, { initialValue: EMPTY_ENTITY_INDEX });
  #servicesWithAccessDetail = toSignal(
    this.#store.pipe(select(selectServicesWithAccessDetail)),
    { initialValue: new Set<ServiceStackTypes>() }
  );
  #servicesWithBackup = toSignal(
    this.#store.pipe(select(selectServicesWithBackup)),
    { initialValue: new Set<ServiceStackTypes>() }
  );
  #servicesWithConfiguration = toSignal(
    this.#store.pipe(select(selectServicesWithConfiguration)),
    { initialValue: new Set<ServiceStackTypes>() }
  );
  query = signal('');
  activeIndex = signal(-1);
  recents = signal<PaletteRecent[]>(this.#readRecents());
  // drill-in scope: palette narrowed to one entity's actions / pages (Tab
  // on an entity row, or seeded from the current route on open); a stack
  // so Backspace pops one level (service → project → global)
  #scopeStack = signal<PaletteScope[]>([]);
  scope = computed<PaletteScope | null>(() => {
    const stack = this.#scopeStack();
    return stack.length ? stack[stack.length - 1] : null;
  });
  #openSig = toSignal(this.open$, { initialValue: false });
  #authed = toSignal(this.#activeClientUser$.pipe(map((d) => !!d)), { initialValue: false });
  #activeClientId = toSignal(
    this.#activeClientUser$.pipe(map((clientUser) => clientUser?.clientId)),
    { initialValue: undefined as string }
  );
  // paste-an-id lookup: containers and build pipelines aren't in any
  // globally subscribed list, so an id-looking query triggers a one-shot
  // exact-match API search on both — ids the local index already knows
  // (projects / services / users) are skipped; deliberately NO
  // distinctUntilChanged: the query resets to '' on every open, and a
  // dedup would swallow a re-paste of the same id right after reopen
  // (stale results rendering, failed lookups becoming unretryable)
  #idLookup = toSignal(
    toObservable(this.query).pipe(
      map((query) => query.trim()),
      debounceTime(ID_LOOKUP_DEBOUNCE),
      switchMap((query) => {
        const clientId = this.#activeClientId();
        if (!clientId || !ID_LIKE.test(query) || this.#isLocallyKnownId(query)) {
          return of(EMPTY_ID_LOOKUP);
        }
        return forkJoin([
          this.#api.searchContainersById$(query, clientId).pipe(
            timeout(ID_LOOKUP_TIMEOUT),
            map((response) => response?.items || []),
            catchError(() => of([]))
          ),
          this.#api.searchAppVersionsById$(query, clientId).pipe(
            timeout(ID_LOOKUP_TIMEOUT),
            map((response) => response?.items || []),
            catchError(() => of([]))
          )
        ]).pipe(
          map(([ containers, appVersions ]): PaletteIdLookup => ({
            forId: query,
            pending: false,
            containers,
            appVersions
          })),
          startWith({ ...EMPTY_ID_LOOKUP, forId: query, pending: true })
        );
      })
    ),
    { initialValue: EMPTY_ID_LOOKUP }
  );
  #entityItems = computed<PaletteItem[]>(() => {
    const { projects, users, activeClientUserId } = this.#entityIndex();
    return this.#buildEntityItems(projects, users, activeClientUserId);
  });
  #entityMetaById = computed<Map<string, PaletteEntityMeta>>(() => {
    const meta = new Map<string, PaletteEntityMeta>();
    this.#entityIndex().projects.forEach((project) => {
      meta.set(project.id, { label: project.name, fullAccess: project.fullAccess });
      project.stacks.forEach((stack) => meta.set(stack.id, {
        label: stack.name,
        parentId: project.id,
        fullAccess: project.fullAccess,
        typeId: stack.typeId,
        typeCategory: stack.typeCategory,
        reloadAvailable: stack.reloadAvailable
      }));
    });
    return meta;
  });
  #contextCommands = computed<PaletteItem[]>(() => {
    const ctx = this.#context();
    const dispatch = (action: Action) => this.#store.dispatch(action);
    const commands: PaletteItem[] = [];
    if (ctx.type === 'service' && ctx.id) {
      commands.push(
        ...this.#buildServiceActions(ctx.id, 'Current service'),
        ...buildServiceContextCommands(ctx.id, dispatch, this.#serviceTabGating(ctx.id))
      );
    }
    const projectId = ctx.type === 'project' ? ctx.id : ctx.projectId;
    if (projectId) {
      commands.push(...buildProjectContextCommands(projectId, dispatch));
    }
    return commands;
  });
  items = computed<PaletteItem[]>(() => [
    ...this.#contextCommands(),
    ...this.#staticCommands(),
    ...this.#entityItems()
  ]);
  #effectiveItems = computed<PaletteItem[]>(() => {
    const scope = this.scope();
    if (!scope) { return this.items(); }
    const dispatch = (action: Action) => this.#store.dispatch(action);
    if (scope.type === 'service') {
      return [
        ...this.#buildServiceActions(scope.id, 'Actions'),
        ...buildServiceContextCommands(scope.id, dispatch, this.#serviceTabGating(scope.id), 'Pages')
      ];
    }
    return [
      ...buildProjectContextCommands(scope.id, dispatch, 'Pages'),
      // the project's own services, reused from the global index — same
      // item ids, so recents and drill-in keep working
      ...this.#entityItems().filter((item) => item.type === 'service' && item.parentId === scope.id)
    ];
  });
  // hits from the id lookup, materialized as top-ranked entries; gated on
  // forId still matching the live query so stale responses never render
  #idLookupEntries = computed<PaletteEntry[]>(() => {
    const lookup = this.#idLookup();
    if (lookup.pending || lookup.forId !== this.query().trim()) { return []; }
    const dispatch = (action: Action) => this.#store.dispatch(action);
    const meta = this.#entityMetaById();
    const sublabelFor = (serviceStackId: string) => {
      const service = meta.get(serviceStackId);
      const project = service?.parentId ? meta.get(service.parentId) : undefined;
      return service
        ? (project ? `${service.label} · ${project.label}` : service.label)
        : undefined;
    };
    const asEntry = (item: PaletteItem): PaletteEntry => ({
      item,
      labelMatches: [],
      sublabelMatches: [],
      score: ID_LOOKUP_SCORE
    });

    return [
      ...lookup.containers.reduce((arr, container) => {
        // system services (project core, L7 balancer) are stripped from
        // the local index, so their containers — the ones support pastes
        // most — fall back to the container's own hostname
        const sublabel = sublabelFor(container.serviceStackId) ?? container.hostname ?? undefined;
        return arr.concat([
          asEntry({
            id: `container-log:${container.id}`,
            type: 'command',
            label: `Container #${container.number} — open logs`,
            sublabel,
            icon: 'subject',
            group: ID_LOOKUP_GROUP,
            run: () => dispatch(zefGo(
              [ '/service-stack', container.serviceStackId, 'log' ],
              { containerId: container.id }
            ))
          }),
          asEntry({
            id: `container-service:${container.id}`,
            type: 'command',
            label: `Container #${container.number} — open service`,
            sublabel,
            icon: 'lan',
            group: ID_LOOKUP_GROUP,
            run: () => dispatch(zefGo([ '/service-stack', container.serviceStackId ]))
          })
        ]);
      }, [] as PaletteEntry[]),
      ...lookup.appVersions.map((appVersion) => asEntry({
        id: `pipeline:${appVersion.id}`,
        type: 'command',
        label: `Pipeline${appVersion.sequence ? ` #${appVersion.sequence}` : ''} — open detail`,
        // triage at a glance: "build failed · app · my-project"
        sublabel: [
          appVersion.status?.toLowerCase().replace(/_/g, ' '),
          sublabelFor(appVersion.serviceStackId)
        ].filter(Boolean).join(' · ') || undefined,
        icon: 'rocket_launch',
        group: ID_LOOKUP_GROUP,
        run: () => dispatch(zefGo([
          '/service-stack', appVersion.serviceStackId, 'deploy', appVersion.id
        ]))
      }))
    ];
  });
  // pending must be derived from the live query, not just the stream —
  // the stream lags a debounce behind, and during that window the empty
  // state would flash a factually wrong "No results" for the pasted id
  idLookupPending = computed(() => {
    const query = this.query().trim();
    // must mirror the stream's guards exactly — any query the stream
    // skips would otherwise read as pending forever
    if (!this.#activeClientId() || !ID_LIKE.test(query) || this.#isLocallyKnownId(query)) {
      return false;
    }
    const lookup = this.#idLookup();
    return lookup.forId !== query || lookup.pending;
  });
  #searchResults = computed<PaletteEntry[]>(() => {
    const items = this.#effectiveItems() || [];
    const query = this.query().trim();
    if (!query) {
      return items.map((item) => ({
        item,
        labelMatches: [] as PaletteMatch[],
        sublabelMatches: [] as PaletteMatch[]
      }));
    }
    // token-AND search: every whitespace-separated token must match the
    // label, sublabel or keywords — so "app zerops legacy" finds service
    // "app" in project "zerops-apps-legacy-prod"
    const tokens = fold(query).split(/\s+/).filter((t) => !!t);
    const recentIds = new Set(this.recents().map((r) => r.id));
    // id-lookup hits carry ID_LOOKUP_SCORE, so the sort pins them on top
    return this.#idLookupEntries().concat(items
      .map((item): PaletteEntry => {
        const scored = this.#scoreItem(item, tokens);
        if (!scored) { return undefined; }
        return {
          item,
          labelMatches: scored.labelRanges.length
            ? [ { key: 'label', value: item.label, indices: scored.labelRanges } ]
            : [],
          sublabelMatches: scored.sublabelRanges.length
            ? [ { key: 'sublabel', value: item.sublabel, indices: scored.sublabelRanges } ]
            : [],
          // higher is better; recently used items get a boost
          score: scored.score + (recentIds.has(item.id) ? RECENT_SCORE_BOOST : 0)
        };
      })
      .filter((entry) => !!entry))
      .sort((a, b) => b.score - a.score);
  });
  grouped = computed<PaletteGroup[]>(() => {
    const entries = this.#searchResults();
    const query = this.query().trim();
    const groups: PaletteGroup[] = [];
    let flatIndex = 0;

    const commit = (group: string, groupEntries: PaletteEntry[], cap?: number) => {
      if (!groupEntries.length) { return; }
      const shown = cap ? groupEntries.slice(0, cap) : groupEntries;
      groups.push({
        group,
        entries: shown.map((entry) => ({ ...entry, flatIndex: flatIndex++ })),
        moreCount: groupEntries.length - shown.length
      });
    };

    const groupRemaining = (source: PaletteEntry[]) => {
      const byGroup = new Map<string, PaletteEntry[]>();
      source.forEach((entry) => {
        if (!byGroup.has(entry.item.group)) {
          byGroup.set(entry.item.group, []);
        }
        byGroup.get(entry.item.group).push(entry);
      });
      return byGroup;
    };

    if (!query) {
      const scoped = !!this.scope();
      const entryById = new Map(entries.map((entry) => [ entry.item.id, entry ]));
      const recent = scoped ? [] : this.recents()
        .slice()
        .sort((a, b) => b.lastUsed - a.lastUsed)
        .map((r) => entryById.get(r.id))
        .filter((entry): entry is PaletteEntry => !!entry)
        .slice(0, RECENTS_SHOWN);
      commit('Recent', recent);

      const recentIds = new Set(recent.map((entry) => entry.item.id));
      const byGroup = groupRemaining(entries.filter((entry) =>
        !recentIds.has(entry.item.id) && (scoped || !entry.item.defaultHidden)
      ));
      [ ...byGroup.keys() ]
        .sort((a, b) => this.#groupOrder(a) - this.#groupOrder(b))
        .forEach((group) => commit(group, byGroup.get(group), scoped ? undefined : EMPTY_STATE_GROUP_CAPS[group]));
    } else {
      // insertion order of first appearance keeps groups ordered
      // by their best-scoring result
      groupRemaining(entries.slice(0, RESULTS_CAP))
        .forEach((groupEntries, group) => commit(group, groupEntries));
    }

    return groups;
  });
  flat = computed<PaletteFlatEntry[]>(() => this.grouped().reduce(
    (arr, group) => arr.concat(group.entries),
    [] as PaletteFlatEntry[]
  ));
  activeIsDrillable = computed(() => {
    const item = this.flat()[this.activeIndex()]?.item;
    if (!item || (item.type !== 'project' && item.type !== 'service')) { return false; }
    // an entity row is drillable unless it IS the current scope already
    const scope = this.scope();
    return !scope || item.id !== `${scope.type}:${scope.id}`;
  });

  // # State resolver
  state = this.$connect({
    open: this.open$
  });

  // # Dispatcher
  constructor() {
    super();

    // hotkeys-js by default ignores events from INPUT/TEXTAREA/SELECT —
    // override so the shortcut works everywhere, restore on destroy
    hotkeys.filter = () => true;
    hotkeys(HOTKEY, (event) => {
      if (!this.#authed()) { return; }
      event.preventDefault();
      this.#store.dispatch(zefDialogToggle({ key: FEATURE_NAME }));
    });

    // reset BEFORE the (deferred) dialog attach so the first paint never
    // flashes the previous session's query/scope; capture and restore the
    // opener's focus (zef-dialog hardcodes restoreFocus: false)
    effect(() => {
      if (this.#openSig()) {
        this.#previouslyFocused = document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
        this.query.set('');
        // seed the scope from the current route so the palette opens
        // narrowed to where the user is (service pages seed
        // [project, service] so ⌫ walks up); untracked — index / route
        // updates while open must not re-seed under the user
        this.#scopeStack.set(untracked(() => this.#seedScopeStack()));
        this.recents.set(this.#readRecents());
        this.#lastPointer = undefined;
        this.#activeItemId = null;
      } else {
        const el = this.#previouslyFocused;
        this.#previouslyFocused = null;
        if (el?.isConnected) {
          setTimeout(() => el.focus());
        }
      }
    }, { allowSignalWrites: true });

    // rebuild the key manager whenever the visible list changes; keep the
    // user's selection when the same item still exists (entity updates
    // mid-navigation), reset to the best result otherwise
    effect(() => {
      const flat = this.flat();
      const options = flat.map((entry) => new PaletteOption(
        entry,
        (active) => {
          this.activeIndex.set(active.flatIndex);
          this.#activeItemId = active.item.id;
          this.#scrollActiveIntoView(active.flatIndex);
        }
      ));
      this.#keyManager = new ActiveDescendantKeyManager(options).withWrap();
      const preservedIndex = this.#activeItemId
        ? flat.findIndex((entry) => entry.item.id === this.#activeItemId)
        : -1;
      if (preservedIndex > -1) {
        this.#keyManager.setActiveItem(preservedIndex);
      } else if (options.length) {
        this.#keyManager.setFirstItemActive();
      } else {
        this.activeIndex.set(-1);
        this.#activeItemId = null;
      }
    }, { allowSignalWrites: true });

    this.$dispatchActions([

      this.onClose$.pipe(
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      this.onRun$.pipe(
        map((item) => {
          // isolated: a throwing command or a localStorage failure must
          // not error the merged dispatcher stream (it would also kill
          // onClose$ and leave the dialog unclosable)
          try {
            // id-lookup rows are one-shot — they can never render as
            // recents, so recording them would only evict real ones
            // out of the capped store
            if (item.group !== ID_LOOKUP_GROUP) {
              this.#recordRecent(item.id);
            }
            item.run();
          } catch (err) {
            console.error('[command-palette] command failed', err);
          }
          return zefDialogClose({ key: FEATURE_NAME });
        })
      )

    ]);
  }

  ngOnDestroy() {
    hotkeys.unbind(HOTKEY);
    hotkeys.filter = this.#previousHotkeysFilter;
    super.ngOnDestroy();
  }

  onDialogOpen() {
    // state is reset by the open$ effect before attach; this only makes
    // sure the input has focus once the content exists
    setTimeout(() => this.searchInputRef()?.nativeElement.focus());
  }

  onQueryChange(value: string) {
    // typing re-ranks — selection intentionally resets to the best result
    this.#activeItemId = null;
    this.query.set(value);
  }

  // keep focus in the input — clicks on chrome (titles, footer, gaps)
  // must not blur the only keydown listener
  onPanelMousedown(event: MouseEvent) {
    if (!(event.target as HTMLElement).closest('.__input')) {
      event.preventDefault();
    }
  }

  onKeydown(event: KeyboardEvent) {
    // never fight the IME: Enter/arrows during composition belong
    // to the candidate list
    if (event.isComposing || event.keyCode === 229) { return; }

    if (event.key === 'Enter') {
      event.preventDefault();
      const entry = this.flat()[this.activeIndex()];
      if (entry) {
        this.onRun$.next(entry.item);
      }
      return;
    }
    if (event.key === 'Tab') {
      // tab never leaves the search input — it either drills into the
      // active entity or is swallowed (the palette is a single-input surface)
      event.preventDefault();
      if (!event.shiftKey && this.activeIsDrillable()) {
        this.#drillInto(this.flat()[this.activeIndex()].item);
      }
      return;
    }
    if (event.key === 'Backspace' && !this.query() && this.scope()) {
      // discrete press only — deleting a query via key auto-repeat must
      // not blow through into popping the scope
      if (!event.repeat) {
        event.preventDefault();
        this.#activeItemId = null;
        // pop one level: service → project → global
        this.#scopeStack.update((stack) => stack.slice(0, -1));
      }
      return;
    }
    if (event.key === 'Home' && !this.query()) {
      event.preventDefault();
      this.#keyManager?.setFirstItemActive();
      return;
    }
    if (event.key === 'End' && !this.query()) {
      event.preventDefault();
      this.#keyManager?.setLastItemActive();
      return;
    }
    // arrows (with wrap-around) are handled by the cdk key manager
    this.#keyManager?.onKeydown(event);
  }

  // chrome fires synthetic mousemove when content scrolls or reflows under
  // a stationary cursor — only honor real pointer movement, otherwise
  // keyboard navigation gets hijacked by hover; the anchor only advances
  // once the threshold is crossed so slow movement still accumulates
  onItemPointerMove(event: MouseEvent, index: number) {
    if (!this.#lastPointer) {
      this.#lastPointer = { x: event.clientX, y: event.clientY };
      return;
    }
    if (
      Math.abs(event.clientX - this.#lastPointer.x) < 2
      && Math.abs(event.clientY - this.#lastPointer.y) < 2
    ) {
      return;
    }
    this.#lastPointer = { x: event.clientX, y: event.clientY };
    if (this.activeIndex() !== index) {
      this.#keyManager?.setActiveItem(index);
    }
  }

  #drillInto(item: PaletteItem) {
    // entity ids are `project:<id>` / `service:<id>`
    const [ type, id ] = item.id.split(':');
    if ((type !== 'project' && type !== 'service') || !id) { return; }
    this.#activeItemId = null;
    // push, don't replace — a service row inside a project scope drills
    // one level deeper and Backspace retraces the path
    this.#scopeStack.update((stack) => [ ...stack, { type, id, label: item.label } ]);
    this.query.set('');
  }

  // page commands for one stack, gated like the service detail menu
  #serviceTabGating(serviceId: string): ServiceTabGating | undefined {
    const meta = this.#entityMetaById().get(serviceId);
    if (!meta?.typeId) { return undefined; }
    return {
      typeCategory: meta.typeCategory,
      hasAccessDetails: this.#servicesWithAccessDetail().has(meta.typeId),
      hasBackup: this.#servicesWithBackup().has(meta.typeId),
      hasConfiguration: this.#servicesWithConfiguration().has(meta.typeId)
    };
  }

  // service action commands for one stack, gated by the entity-index
  // metadata (write access, category exclusions, reload availability)
  #buildServiceActions(serviceId: string, group: string): PaletteItem[] {
    const meta = this.#entityMetaById().get(serviceId);
    return buildServiceActionCommands(
      serviceId,
      (action: Action) => this.#store.dispatch(action),
      group,
      {
        fullAccess: meta?.fullAccess ?? false,
        typeCategory: meta?.typeCategory,
        reloadAvailable: meta?.reloadAvailable
      }
    );
  }

  // ids the local index already surfaces through keywords (projects,
  // services, team members) — no point hitting the API for those
  #isLocallyKnownId(id: string): boolean {
    if (this.#entityMetaById().has(id)) { return true; }
    return this.#entityIndex().users.some((user) => user.id === id || user.userId === id);
  }

  #seedScopeStack(): PaletteScope[] {
    const ctx = this.#context();
    if (ctx.type === 'none' || !ctx.id) { return []; }
    const meta = this.#entityMetaById();
    const label = meta.get(ctx.id)?.label;
    // entity name not in the index yet — open unscoped rather than seed
    // a chip with no label
    if (!label) { return []; }
    if (ctx.type === 'project') {
      return [ { type: 'project', id: ctx.id, label } ];
    }
    const serviceScope: PaletteScope = { type: 'service', id: ctx.id, label };
    const projectId = meta.get(ctx.id)?.parentId ?? ctx.projectId;
    const projectLabel = projectId ? meta.get(projectId)?.label : undefined;
    // parent project known — seed [project, service] so ⌫ lands on the
    // project first, then global
    return projectId && projectLabel
      ? [ { type: 'project', id: projectId, label: projectLabel }, serviceScope ]
      : [ serviceScope ];
  }

  // token must substring-match one of the (diacritics-folded) fields;
  // label matches score highest, prefix matches get a bonus, matched
  // ranges drive highlighting
  #scoreItem(item: PaletteItem, tokens: string[]): ScoredItem | undefined {
    const label = fold(item.label);
    const sublabel = item.sublabel ? fold(item.sublabel) : '';
    const keywords = item.keywords ? fold(item.keywords) : '';
    let score = 0;
    const labelRanges: number[][] = [];
    const sublabelRanges: number[][] = [];

    for (const token of tokens) {
      const labelIndex = label.indexOf(token);
      if (labelIndex > -1) {
        score += 1 + token.length / label.length + (labelIndex === 0 ? 0.5 : 0);
        labelRanges.push([ labelIndex, labelIndex + token.length - 1 ]);
        continue;
      }
      const sublabelIndex = sublabel.indexOf(token);
      if (sublabelIndex > -1) {
        score += 0.6 + (sublabelIndex === 0 ? 0.2 : 0);
        sublabelRanges.push([ sublabelIndex, sublabelIndex + token.length - 1 ]);
        continue;
      }
      if (keywords.includes(token)) {
        score += 0.4;
        continue;
      }
      return undefined;
    }

    return {
      score,
      labelRanges: this.#mergeRanges(labelRanges),
      sublabelRanges: this.#mergeRanges(sublabelRanges)
    };
  }

  #mergeRanges(ranges: number[][]): number[][] {
    if (ranges.length < 2) { return ranges; }
    const sorted = ranges.slice().sort((a, b) => a[0] - b[0]);
    const merged = [ sorted[0] ];
    for (const [ start, end ] of sorted.slice(1)) {
      const last = merged[merged.length - 1];
      if (start <= last[1] + 1) {
        last[1] = Math.max(last[1], end);
      } else {
        merged.push([ start, end ]);
      }
    }
    return merged;
  }

  // raw entity ids ride along in `keywords` — the scorer substring-matches
  // folded keywords, so pasting a full or partial id (the support /
  // impersonation flow) ranks the entity
  #buildEntityItems(
    projects: PaletteEntityIndex['projects'],
    users: PaletteEntityIndex['users'],
    activeClientUserId?: string
  ): PaletteItem[] {
    const dispatch = (action: Action) => this.#store.dispatch(action);

    const projectItems = projects.map((project): PaletteItem => ({
      id: `project:${project.id}`,
      type: 'project',
      label: project.name,
      icon: 'dashboard',
      keywords: project.id,
      group: 'Projects',
      run: () => dispatch(zefGo([ '/project', project.id ]))
    }));

    const serviceItems = projects.reduce((arr, project) => arr.concat(
      project.stacks.map((stack): PaletteItem => ({
        id: `service:${stack.id}`,
        type: 'service',
        label: stack.name,
        sublabel: project.name,
        keywords: stack.typeId ? `${stack.typeId} ${stack.id}` : stack.id,
        icon: 'lan',
        group: 'Services',
        parentId: project.id,
        run: () => dispatch(zefGo([ '/service-stack', stack.id ]))
      }))
    ), [] as PaletteItem[]);

    const userItems = users.map((user): PaletteItem => ({
      id: `user:${user.id}`,
      type: 'user',
      label: user.name,
      sublabel: user.email,
      icon: 'person',
      // both ids — support staff find users by clientUser id or user id
      // after impersonation
      keywords: user.userId ? `${user.id} ${user.userId}` : user.id,
      group: 'Team',
      // the product UI routes the active user to their own settings,
      // not the admin edit page — mirror that
      run: () => user.id === activeClientUserId
        ? dispatch(zefGo([ '/settings', 'account' ]))
        : dispatch(zefGo([ '/dashboard', 'user-edit', user.id ]))
    }));

    return [ ...projectItems, ...serviceItems, ...userItems ];
  }

  #groupOrder(group: string) {
    const index = GROUP_ORDER.indexOf(group);
    return index === -1 ? GROUP_ORDER.length : index;
  }

  #scrollActiveIntoView(index: number) {
    setTimeout(() => document
      .getElementById(`z-palette-opt-${index}`)
      ?.scrollIntoView({ block: 'nearest' })
    );
  }

  #readRecents(): PaletteRecent[] {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
      if (!Array.isArray(raw)) { return []; }
      // one malformed entry (schema drift, other tabs, manual edits) must
      // not throw inside the computeds that consume this
      return raw.filter((r): r is PaletteRecent =>
        !!r && typeof r.id === 'string' && typeof r.lastUsed === 'number'
      );
    } catch {
      return [];
    }
  }

  #recordRecent(id: string) {
    const recents = this.#readRecents();
    const existing = recents.find((r) => r.id === id);
    const updated = [
      { id, count: (existing?.count || 0) + 1, lastUsed: Date.now() },
      ...recents.filter((r) => r.id !== id)
    ]
      .sort((a, b) => b.lastUsed - a.lastUsed)
      .slice(0, RECENTS_CAP);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
    this.recents.set(updated);
  }

}
